// BiomeBackdrop.jsx
// Full-bleed painted scenes for the Bloom & Blight battle stage — one per
// biome, layered SVG with depth. Shared stage geometry (1600×900, bottom
// anchored): the enemy ground band runs from y≈500 (their feet line) down to
// y≈640, where the foreground band begins — your team stands around y≈620–640
// with the dock UI over the lower quarter. Keep those lines when adding scenes.

import React from "react";

const SVG = ({ children }) => (
  <svg
    className="bs-scene"
    viewBox="0 0 1600 900"
    preserveAspectRatio="xMidYMax slice"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/* ------------------------------------------------ Carmel-by-the-Sea 🌊 */
const Carmel = () => (
  <SVG>
    <defs>
      <linearGradient id="carSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8fd0e8" />
        <stop offset="0.62" stopColor="#cdeef2" />
        <stop offset="1" stopColor="#fdf3d8" />
      </linearGradient>
      <linearGradient id="carSea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#3f96ad" />
        <stop offset="1" stopColor="#7cc4d4" />
      </linearGradient>
      <radialGradient id="carSun" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#fff7d9" />
        <stop offset="0.55" stopColor="#ffe9a8" stopOpacity="0.9" />
        <stop offset="1" stopColor="#ffe9a8" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="1600" height="900" fill="url(#carSky)" />
    <circle cx="1240" cy="150" r="140" fill="url(#carSun)" />
    <circle cx="1240" cy="150" r="52" fill="#fff3c4" />
    {/* sea */}
    <rect y="360" width="1600" height="150" fill="url(#carSea)" />
    <path d="M0 370 Q200 362 400 370 T800 370 T1200 370 T1600 370 V360 H0 Z" fill="#dff2f5" opacity="0.7" />
    <path d="M120 420 q60 -10 120 0 M520 455 q70 -12 140 0 M1050 415 q65 -10 130 0" stroke="#cdeef2" strokeWidth="6" fill="none" opacity="0.55" strokeLinecap="round" />
    {/* headland + cypress */}
    <path d="M0 400 Q150 316 340 360 L340 512 L0 512 Z" fill="#4e7a54" />
    <path d="M0 408 Q130 342 300 376 L300 512 L0 512 Z" fill="#3c6244" opacity="0.85" />
    <g fill="#2e4f36">
      <path d="M180 352 q30 -60 60 0 q-14 -8 -30 -8 q-16 0 -30 8 Z" />
      <rect x="204" y="334" width="8" height="44" fill="#4a3a28" />
      <path d="M250 372 q26 -48 52 0 q-12 -7 -26 -7 t-26 7 Z" />
      <rect x="272" y="362" width="7" height="34" fill="#4a3a28" />
    </g>
    {/* gulls */}
    <path d="M980 190 q12 -12 24 0 q12 -12 24 0 M1120 240 q10 -10 20 0 q10 -10 20 0" stroke="#6b7d83" strokeWidth="5" fill="none" strokeLinecap="round" />
    {/* dune ridge — the enemy team stands along y≈500 */}
    <path d="M0 512 Q300 470 700 496 T1600 484 V900 H0 Z" fill="#eed7a6" />
    <path d="M0 520 Q320 480 720 504 T1600 492" stroke="#dcbf8a" strokeWidth="8" fill="none" opacity="0.6" />
    <g stroke="#b9a05f" strokeWidth="5" strokeLinecap="round" opacity="0.8">
      <path d="M140 502 q-6 -26 -16 -34 M148 502 q0 -30 6 -40 M156 502 q8 -24 18 -30" fill="none" />
      <path d="M1380 492 q-6 -24 -14 -32 M1388 492 q2 -28 8 -36 M1396 492 q8 -22 16 -28" fill="none" />
    </g>
    <g fill="#dcbf8a" opacity="0.7">
      <ellipse cx="560" cy="560" rx="34" ry="8" />
      <ellipse cx="1180" cy="548" rx="28" ry="7" />
    </g>
    {/* foreground beach — your team stands around y≈630 */}
    <path d="M0 646 Q400 608 900 632 T1600 620 V900 H0 Z" fill="#f4e2b8" />
    <path d="M0 660 Q420 622 920 646 T1600 632" stroke="#e3c98d" strokeWidth="10" fill="none" opacity="0.7" />
    <g fill="#d9b97f" opacity="0.85">
      <ellipse cx="240" cy="760" rx="30" ry="8" />
      <ellipse cx="1180" cy="800" rx="38" ry="9" />
      <ellipse cx="760" cy="852" rx="26" ry="7" />
    </g>
    <g fill="#e8788f" opacity="0.9">
      <circle cx="88" cy="700" r="7" /><circle cx="104" cy="690" r="5" />
      <circle cx="1496" cy="686" r="6" /><circle cx="1510" cy="698" r="5" />
    </g>
    <g stroke="#c9ae72" strokeWidth="5" strokeLinecap="round" opacity="0.8" fill="none">
      <path d="M340 718 q-5 -22 -13 -28 M348 718 q0 -26 5 -33 M356 718 q7 -20 14 -25" />
    </g>
  </SVG>
);

/* --------------------------------------------------- Sonoran Desert 🌵 */
const Sonoran = () => (
  <SVG>
    <defs>
      <linearGradient id="sonSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffca7a" />
        <stop offset="0.55" stopColor="#ffe3a6" />
        <stop offset="1" stopColor="#fff3cf" />
      </linearGradient>
      <radialGradient id="sonSun" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#fff2c8" />
        <stop offset="0.5" stopColor="#ffd98e" stopOpacity="0.85" />
        <stop offset="1" stopColor="#ffd98e" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="1600" height="900" fill="url(#sonSky)" />
    <circle cx="330" cy="170" r="180" fill="url(#sonSun)" />
    <circle cx="330" cy="170" r="66" fill="#fff0b8" />
    {/* mesas */}
    <path d="M950 380 h180 l30 -90 h60 l26 90 h200 V520 H950 Z" fill="#d99a66" />
    <path d="M980 380 h520 v14 H980 Z" fill="#e8b183" opacity="0.8" />
    <path d="M1180 290 h60 v10 h-60 Z" fill="#e8b183" opacity="0.7" />
    <path d="M0 404 h240 l22 -64 h48 l20 64 h130 V540 H0 Z" fill="#cf8a58" />
    {/* far dunes */}
    <path d="M0 460 Q380 416 820 448 T1600 430 V900 H0 Z" fill="#eab26e" />
    {/* enemy ridge — feet line y≈500 */}
    <path d="M0 512 Q420 468 900 498 T1600 486 V900 H0 Z" fill="#e0a256" />
    <path d="M0 520 Q440 478 920 506 T1600 494" stroke="#c98d4e" strokeWidth="8" fill="none" opacity="0.55" />
    {/* saguaros rooted on the ridge */}
    <g fill="#8a6a3f">
      <path d="M1445 372 q0 -84 22 -84 q22 0 22 84 l0 116 h-44 Z M1433 336 q-20 0 -20 -32 q0 -17 10 -17 q10 0 10 17 l0 18 q0 8 10 8 Z M1500 356 q22 0 22 -38 q0 -19 -11 -19 q-11 0 -11 19 l0 24 q0 8 -11 8 Z" />
      <path d="M120 410 q0 -66 17 -66 q17 0 17 66 l0 80 h-34 Z M111 386 q-16 0 -16 -25 q0 -13 8 -13 q8 0 8 13 l0 13 q0 7 8 7 Z" opacity="0.9" />
    </g>
    <g fill="#c98d4e" opacity="0.8">
      <ellipse cx="620" cy="562" rx="36" ry="8" />
      <ellipse cx="1120" cy="550" rx="30" ry="7" />
    </g>
    {/* foreground desert floor — your team around y≈630 */}
    <path d="M0 648 Q420 610 940 636 T1600 622 V900 H0 Z" fill="#f2c987" />
    <path d="M0 662 Q440 624 960 650 T1600 636" stroke="#dfae67" strokeWidth="10" fill="none" opacity="0.7" />
    <g fill="#c99a5d">
      <ellipse cx="300" cy="762" rx="32" ry="8" />
      <ellipse cx="1240" cy="796" rx="40" ry="9" />
    </g>
    <g stroke="#a97e42" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.85">
      <path d="M520 728 q-4 -20 -12 -26 M528 728 q0 -24 5 -30 M536 728 q6 -18 13 -22" />
      <path d="M1420 712 q-4 -18 -10 -24 M1428 712 q0 -22 4 -28 M1436 712 q6 -16 12 -20" />
    </g>
    <g fill="#b3552f" opacity="0.9">
      <circle cx="1090" cy="702" r="6" /><circle cx="1104" cy="694" r="4" />
    </g>
  </SVG>
);

/* -------------------------------------------------- Mossy Northwest 🌲 */
const Mosswood = () => (
  <SVG>
    <defs>
      <linearGradient id="mossSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#b7d3c4" />
        <stop offset="0.6" stopColor="#dcebd9" />
        <stop offset="1" stopColor="#eef5e6" />
      </linearGradient>
    </defs>
    <rect width="1600" height="900" fill="url(#mossSky)" />
    <circle cx="1180" cy="170" r="56" fill="#f2f7ea" opacity="0.9" />
    {/* conifer ridges, far → near */}
    <g fill="#89ab8d" opacity="0.75">
      {[0, 200, 400, 600, 800, 1000, 1200, 1400].map((x) => (
        <path key={x} d={`M${x} 330 l50 -104 l50 104 l40 -80 l46 80 Z`} />
      ))}
      <rect y="328" width="1600" height="56" />
    </g>
    <rect y="348" width="1600" height="40" fill="#fff" opacity="0.35" />
    <g fill="#5d8a68" opacity="0.9">
      {[-60, 160, 380, 600, 820, 1040, 1260, 1480].map((x) => (
        <path key={x} d={`M${x} 428 l64 -140 l64 140 l52 -102 l58 102 Z`} />
      ))}
      <rect y="424" width="1600" height="64" />
    </g>
    <rect y="444" width="1600" height="38" fill="#eef5e6" opacity="0.4" />
    {/* enemy ledge — mossy shelf, feet line y≈500 */}
    <path d="M0 512 Q360 478 820 500 T1600 488 V900 H0 Z" fill="#6f9663" />
    <path d="M0 520 Q380 488 840 510 T1600 498" stroke="#557d4a" strokeWidth="9" fill="none" opacity="0.6" />
    <g fill="#3f6d4e">
      <path d="M60 498 l38 -88 l38 88 Z M84 490 l30 -66 l30 66 Z" opacity="0.85" />
      <path d="M1430 490 l42 -96 l42 96 Z" opacity="0.85" />
    </g>
    <g fill="#557d4a" opacity="0.8">
      <ellipse cx="600" cy="560" rx="38" ry="9" />
      <ellipse cx="1150" cy="548" rx="30" ry="8" />
    </g>
    {/* foreground mossy ground — your team around y≈630 */}
    <path d="M0 648 Q420 612 940 636 T1600 624 V900 H0 Z" fill="#557d4a" />
    <path d="M0 662 Q440 626 960 650 T1600 636" stroke="#3f6d4e" strokeWidth="10" fill="none" opacity="0.65" />
    <g stroke="#7fae6d" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.9">
      <path d="M180 736 q-14 -30 -38 -38 M180 736 q-2 -34 -16 -48 M180 736 q10 -30 30 -40 M180 736 q22 -22 44 -26" />
      <path d="M1330 722 q-12 -26 -32 -34 M1330 722 q0 -30 -12 -42 M1330 722 q10 -26 26 -34 M1330 722 q20 -18 38 -22" />
    </g>
    <g fill="#7d8a77">
      <ellipse cx="700" cy="770" rx="42" ry="14" />
      <ellipse cx="744" cy="778" rx="24" ry="9" />
    </g>
    {/* hanging mist */}
    <rect y="380" width="1600" height="24" fill="#fff" opacity="0.22" />
    <rect y="540" width="1600" height="18" fill="#fff" opacity="0.16" />
  </SVG>
);

/* ------------------------------------------------ The Old Glasshouse 🏡 */
const Glasshouse = () => (
  <SVG>
    <defs>
      <linearGradient id="ghSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#bfe0ee" />
        <stop offset="0.7" stopColor="#e6f2de" />
        <stop offset="1" stopColor="#f4ecd2" />
      </linearGradient>
      <linearGradient id="ghWood" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#caa26c" />
        <stop offset="1" stopColor="#a97e4d" />
      </linearGradient>
    </defs>
    <rect width="1600" height="900" fill="url(#ghSky)" />
    {/* glass roof + mullions */}
    <g stroke="#7a6248" strokeWidth="10">
      <path d="M-20 280 L800 30 L1620 280" fill="none" />
      <path d="M800 30 L800 280" />
    </g>
    <g stroke="#7a6248" strokeWidth="6" opacity="0.85">
      {[160, 360, 560, 1040, 1240, 1440].map((x) => (
        <path key={x} d={`M${x} ${280 - Math.abs(800 - x) * 0.22 + 56} L${x} 500`} />
      ))}
      <path d="M-20 280 H1620 M-20 400 H1620" />
    </g>
    <g stroke="#ffffff" strokeWidth="3" opacity="0.5">
      <path d="M120 80 L410 320 M1180 110 L1480 350" />
    </g>
    {/* hanging plants */}
    <g>
      <path d="M480 280 v50 M1120 280 v40" stroke="#6b5138" strokeWidth="5" />
      <path d="M450 330 h60 l-8 32 h-44 Z" fill="#b3552f" />
      <path d="M446 334 q-20 38 -30 70 M470 340 q-6 42 -16 74 M510 334 q16 36 26 66" stroke="#4c7c34" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M1096 320 h48 l-7 26 h-34 Z" fill="#8a6a3f" />
      <path d="M1094 324 q-16 32 -24 56 M1130 326 q10 30 18 54" stroke="#609a48" strokeWidth="5" fill="none" strokeLinecap="round" />
    </g>
    {/* back bench — enemy feet line at y≈500 */}
    <rect x="0" y="486" width="1600" height="28" fill="url(#ghWood)" />
    <rect x="0" y="510" width="1600" height="12" fill="#8a6440" />
    <g fill="#8a6440">
      <rect x="140" y="518" width="22" height="110" /><rect x="740" y="518" width="22" height="110" /><rect x="1400" y="518" width="22" height="110" />
    </g>
    {/* bench clutter */}
    <g opacity="0.9">
      <path d="M40 458 h44 l-6 28 h-32 Z" fill="#c96b48" />
      <path d="M46 454 q-10 -26 8 -40 q-2 22 6 30 q4 -24 16 -30 q0 20 6 30 q10 -10 12 -22 q8 18 -6 32 Z" fill="#609a48" />
      <path d="M1508 454 h48 l-7 30 h-34 Z" fill="#7d8a77" />
      <path d="M1516 450 q0 -30 16 -38 q-4 22 2 30 q8 -18 18 -22 q-4 20 -2 30 Z" fill="#4c7c34" />
    </g>
    {/* floor glimpse between bench and table */}
    <rect x="0" y="514" width="1600" height="130" fill="#d9c396" />
    <g stroke="#c4ac7c" strokeWidth="4" opacity="0.7">
      <path d="M0 560 H1600 M0 606 H1600" />
    </g>
    {/* foreground work table — your team around y≈630 */}
    <path d="M0 644 H1600 V900 H0 Z" fill="url(#ghWood)" />
    <path d="M0 644 H1600 V660 H0 Z" fill="#e0b984" opacity="0.7" />
    <g stroke="#8a6440" strokeWidth="4" opacity="0.6">
      <path d="M220 660 V900 M640 660 V900 M1060 660 V900 M1480 660 V900" />
    </g>
    <g opacity="0.85">
      <ellipse cx="1310" cy="700" rx="46" ry="12" fill="#8a6440" />
      <path d="M1282 692 q28 -14 56 0 l-6 8 q-22 -10 -44 0 Z" fill="#c9c9c9" />
      <circle cx="120" cy="712" r="10" fill="#d98354" /><circle cx="146" cy="720" r="7" fill="#d98354" />
    </g>
  </SVG>
);

const SCENES = { carmel: Carmel, sonoran: Sonoran, mosswood: Mosswood, glasshouse: Glasshouse };

export default function BiomeBackdrop({ biomeId }) {
  const Scene = SCENES[biomeId] || Glasshouse;
  return (
    <div className="bs-backdrop">
      <Scene />
    </div>
  );
}
